// src/pages/BlogPostPage.jsx
// Single blog post: design aligned with FAQ/About (white, geometric background, font-space, black accents).
// PDF CTA in thumb zone when available.
import React, { Suspense, lazy, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, FileDown } from 'lucide-react';
import { getPostBySlug } from '../data/blogPosts';
import { GERMAN_BLOG, hasGermanBlog } from '../data/germanBlog';
import TelcB1RedemittelPost from '../components/blog/TelcB1RedemittelPost';
import TelcB2RedemittelPost from '../components/blog/TelcB2RedemittelPost';
import TelcC1RedemittelPost from '../components/blog/TelcC1RedemittelPost';
import GoetheB1RedemittelPost from '../components/blog/GoetheB1RedemittelPost';
import GoetheB2RedemittelPost from '../components/blog/GoetheB2RedemittelPost';
import GoetheC1RedemittelPost from '../components/blog/GoetheC1RedemittelPost';
import OsdB1RedemittelPost from '../components/blog/OsdB1RedemittelPost';
import OsdB2RedemittelPost from '../components/blog/OsdB2RedemittelPost';
import OsdC1RedemittelPost from '../components/blog/OsdC1RedemittelPost';
import GoetheVsTelcPost from '../components/blog/GoetheVsTelcPost';
import GoetheB2ExamGuidePost from '../components/blog/GoetheB2ExamGuidePost';
import TelcB1ExamGuidePost from '../components/blog/TelcB1ExamGuidePost';
import TelcB2ExamGuidePost from '../components/blog/TelcB2ExamGuidePost';
import TelcC1ExamGuidePost from '../components/blog/TelcC1ExamGuidePost';
import GoetheB1ExamGuidePost from '../components/blog/GoetheB1ExamGuidePost';
import GoetheC1ExamGuidePost from '../components/blog/GoetheC1ExamGuidePost';
import OsdC1ExamGuidePost from '../components/blog/OsdC1ExamGuidePost';
import OsdB2ExamGuidePost from '../components/blog/OsdB2ExamGuidePost';
import OsdB1ExamGuidePost from '../components/blog/OsdB1ExamGuidePost';
import TelcB2SchreibenGuidePost from '../components/blog/TelcB2SchreibenGuidePost';
import TelcB2SchreibenGuidePostDe from '../components/blog/TelcB2SchreibenGuidePostDe';
import TelcB1SchreibenGuidePost from '../components/blog/TelcB1SchreibenGuidePost';
import TelcC1SchreibenGuidePost from '../components/blog/TelcC1SchreibenGuidePost';
import MockVsRealExamPost from '../components/blog/MockVsRealExamPost';
import PrepTimePost from '../components/blog/PrepTimePost';
import { useLangParam } from '../hooks/useLocalizedNavigate';
import MockExamCTA from '../components/blog/MockExamCTA';

// Sprechen sample posts: the component and each exam's data (dialogues, sheets,
// phrases) load as their own chunk, so the text of nine posts never lands in the
// main bundle. The post marks itself with data-prerender-ready once mounted, and
// scripts/prerender-playwright.js waits for that before capturing the HTML.
const sprechenSamplesPost = (loadData) =>
  lazy(() =>
    Promise.all([import('../components/blog/SprechenSamplesPost'), loadData()]).then(([component, data]) => {
      const Post = component.default;
      const samples = data.default;
      return { default: (props) => <Post {...props} data={samples} /> };
    })
  );
const TelcB1SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/telcB1'));
const TelcB2SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/telcB2'));
const TelcC1SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/telcC1'));
const GoetheB1SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/goetheB1'));
const GoetheB2SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/goetheB2'));
const GoetheC1SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/goetheC1'));
const OsdB1SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/osdB1'));
const OsdB2SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/osdB2'));
const OsdC1SprechenSamplesPost = sprechenSamplesPost(() => import('../data/sprechenSamples/osdC1'));

const BLOG_CONTENT_MAP = {
  'telc-b1-redemittel-schreiben': TelcB1RedemittelPost,
  'telc-b2-redemittel-schreiben': TelcB2RedemittelPost,
  'telc-c1-redemittel-schreiben': TelcC1RedemittelPost,
  'goethe-b1-redemittel-schreiben': GoetheB1RedemittelPost,
  'goethe-b2-redemittel-schreiben': GoetheB2RedemittelPost,
  'goethe-c1-redemittel-schreiben': GoetheC1RedemittelPost,
  'osd-b1-redemittel-schreiben': OsdB1RedemittelPost,
  'osd-b2-redemittel-schreiben': OsdB2RedemittelPost,
  'osd-c1-redemittel-schreiben': OsdC1RedemittelPost,
  'goethe-vs-telc': GoetheVsTelcPost,
  'goethe-b2-exam-guide': GoetheB2ExamGuidePost,
  'telc-b1-exam-guide': TelcB1ExamGuidePost,
  'telc-b2-exam-guide': TelcB2ExamGuidePost,
  'telc-c1-exam-guide': TelcC1ExamGuidePost,
  'goethe-b1-exam-guide': GoetheB1ExamGuidePost,
  'goethe-c1-exam-guide': GoetheC1ExamGuidePost,
  'osd-c1-exam-guide': OsdC1ExamGuidePost,
  'osd-b2-exam-guide': OsdB2ExamGuidePost,
  'osd-b1-exam-guide': OsdB1ExamGuidePost,
  'telc-b2-schreiben-guide': TelcB2SchreibenGuidePost,
  'telc-b1-schreiben-guide': TelcB1SchreibenGuidePost,
  'telc-c1-schreiben-guide': TelcC1SchreibenGuidePost,
  'online-mock-exams-vs-real-exam': MockVsRealExamPost,
  'how-long-prepare-german-exam': PrepTimePost,
  'telc-b1-sprechen-sample-exams': TelcB1SprechenSamplesPost,
  'telc-b2-sprechen-sample-exams': TelcB2SprechenSamplesPost,
  'telc-c1-sprechen-sample-exams': TelcC1SprechenSamplesPost,
  'goethe-b1-sprechen-sample-exams': GoetheB1SprechenSamplesPost,
  'goethe-b2-sprechen-sample-exams': GoetheB2SprechenSamplesPost,
  'goethe-c1-sprechen-sample-exams': GoetheC1SprechenSamplesPost,
  'osd-b1-sprechen-sample-exams': OsdB1SprechenSamplesPost,
  'osd-b2-sprechen-sample-exams': OsdB2SprechenSamplesPost,
  'osd-c1-sprechen-sample-exams': OsdC1SprechenSamplesPost,
};

// German bodies, keyed like BLOG_CONTENT_MAP. Every slug here must also be in
// GERMAN_BLOG (src/data/germanBlog.js), which carries the German title and meta.
const BLOG_CONTENT_MAP_DE = {
  'telc-b2-schreiben-guide': TelcB2SchreibenGuidePostDe,
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const lang = useLangParam();
  const { t } = useTranslation();
  const basePost = slug ? getPostBySlug(slug) : null;
  // A translated post is its own page (/de/blog/:slug, self-canonical); every other
  // /{lang}/blog/:slug shows the English post and canonicalizes to /en/.
  const german = Boolean(basePost && lang === 'de' && hasGermanBlog(basePost.slug));
  const post = german ? { ...basePost, ...GERMAN_BLOG[basePost.slug] } : basePost;
  const contentLang = german ? 'de' : 'en';
  const ContentComponent = post && (german ? BLOG_CONTENT_MAP_DE : BLOG_CONTENT_MAP)[post.slug];

  // SEO: document title, meta description, canonical, Open Graph, Twitter Card (absolute URLs)
  const baseUrl = 'https://logami.app';
  useEffect(() => {
    if (!post) return;
    const title = `${post.title} | Logami`;
    // Blog content is English-only except for the slugs in GERMAN_BLOG; every other language
    // variant canonicalizes to /en/ so Google indexes one authoritative page instead of
    // treating each /{lang}/ copy as a duplicate.
    const canonicalUrl = `${baseUrl}/${contentLang}/blog/${post.slug}`;
    // The SERP description, not the blog-index excerpt. Without this, React would
    // hydrate over the description scripts/prerender.js baked in and hand Google
    // back the card copy — which is written for a different job entirely.
    const metaDescription = post.metaDescription || post.excerpt || title;
    const ogImage = post.image ? `${baseUrl}${post.image.split('?')[0]}` : `${baseUrl}/logo-icon.png`;

    const prevTitle = document.title;
    document.title = title;

    let metaDesc = document.querySelector('meta[name="description"]');
    const createdMetaDesc = !metaDesc;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    const prevDescContent = metaDesc.getAttribute('content') || '';
    metaDesc.setAttribute('content', metaDescription);

    let linkCanonical = document.querySelector('link[rel="canonical"]');
    const prevCanonicalHref = linkCanonical?.getAttribute('href') || '';
    if (!linkCanonical) {
      linkCanonical = document.createElement('link');
      linkCanonical.setAttribute('rel', 'canonical');
      document.head.appendChild(linkCanonical);
    }
    linkCanonical.setAttribute('href', canonicalUrl);

    const ogTags = [
      { property: 'og:type', content: 'article' },
      { property: 'og:title', content: title },
      { property: 'og:description', content: metaDescription },
      { property: 'og:image', content: ogImage },
      { property: 'og:url', content: canonicalUrl },
    ];
    const twitterTags = [
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: title },
      { name: 'twitter:description', content: metaDescription },
      { name: 'twitter:image', content: ogImage },
    ];
    const defaultOg = {
      'og:type': 'website',
      'og:title': 'Logami – Pass TELC, Goethe & ÖSD German Exams',
      'og:description': 'Logami is the only B1–C1 platform built by actual German teachers. Stop relying on outdated PDFs! Practice with authentic TELC, Goethe, and ÖSD mock exams.',
      'og:image': `${baseUrl}/logo-icon.png?v=5`,
      'og:url': `${baseUrl}/`,
    };
    const defaultTwitter = {
      'twitter:card': 'summary_large_image',
      'twitter:title': defaultOg['og:title'],
      'twitter:description': defaultOg['og:description'],
      'twitter:image': defaultOg['og:image'],
    };

    const setMeta = (attr, key, value) => {
      const el = document.querySelector(attr === 'property' ? `meta[property="${key}"]` : `meta[name="${key}"]`);
      if (el) el.setAttribute('content', value);
      else {
        const m = document.createElement('meta');
        if (attr === 'property') m.setAttribute('property', key);
        else m.setAttribute('name', key);
        m.setAttribute('content', value);
        document.head.appendChild(m);
      }
    };
    ogTags.forEach(({ property, content }) => setMeta('property', property, content));
    twitterTags.forEach(({ name, content }) => setMeta('name', name, content));

    // Article + BreadcrumbList JSON-LD for rich results in search
    const graphLd = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          inLanguage: contentLang,
          headline: post.title,
          description: metaDescription,
          image: ogImage,
          url: canonicalUrl,
          datePublished: post.date,
          dateModified: post.dateModified || post.date,
          author: {
            '@type': 'Organization',
            name: 'Logami',
            url: baseUrl,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Logami',
            logo: { '@type': 'ImageObject', url: `${baseUrl}/logo-icon.png` },
          },
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Blog', item: `${baseUrl}/${lang}/blog` },
            { '@type': 'ListItem', position: 2, name: post.title, item: canonicalUrl },
          ],
        },
      ],
    };
    const scriptLd = document.createElement('script');
    scriptLd.type = 'application/ld+json';
    scriptLd.textContent = JSON.stringify(graphLd);
    scriptLd.setAttribute('data-seo-article', '');
    document.head.appendChild(scriptLd);

    return () => {
      document.title = prevTitle;
      if (createdMetaDesc && metaDesc?.parentNode) metaDesc.remove();
      else if (metaDesc) metaDesc.setAttribute('content', prevDescContent);
      if (linkCanonical) linkCanonical.setAttribute('href', prevCanonicalHref || `${baseUrl}/`);
      Object.entries(defaultOg).forEach(([k, v]) => setMeta('property', k, v));
      Object.entries(defaultTwitter).forEach(([k, v]) => setMeta('name', k, v));
      const existing = document.querySelector('script[data-seo-article]');
      if (existing?.parentNode) existing.remove();
    };
  }, [post, lang, contentLang]);

  if (!post) {
    return <Navigate to={`/${lang}/blog`} replace />;
  }

  const blogPath = `/${lang}/blog`;
  const hasPdf = Boolean(post.pdfUrl);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Back navigation – clean, like About */}
      <div className="relative z-10 bg-white border-b border-black/10 sticky top-0 safe-area-inset-top">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3">
          <Link
            to={blogPath}
            className="inline-flex items-center gap-1 text-black/70 hover:text-black font-space font-bold text-sm transition-colors min-h-[48px] min-w-[48px] justify-center -ml-2"
            aria-label={t('blog.post.back', { defaultValue: 'Back to Blog' })}
          >
            <ChevronLeft className="w-5 h-5" />
            <span>{t('blog.post.back', { defaultValue: 'Back to Blog' })}</span>
          </Link>
        </div>
      </div>

      {/* Hero image */}
      {post.image && (
        <div className="relative z-10 max-w-3xl mx-auto w-full px-4 sm:px-6 pt-6">
          <img
            src={post.image}
            alt={post.imageAlt || post.title}
            className="w-full aspect-[16/10] object-cover rounded-2xl border border-black/10 shadow-md bg-gray-100"
          />
        </div>
      )}

      {/* Article section – clean like About chapters: solid gradient, no grid over text */}
      {/*
        Entrance is a CSS animation, not an IntersectionObserver opacity gate.
        The observer version resolved to `opacity-0 translate-y-6` in the static
        HTML that scripts/prerender-playwright.js captures, so every blog post was
        being served to non-JS crawlers with its entire article invisible —
        verified live on /en/blog/telc-b1-redemittel-schreiben. animate-fade-in-up
        rests visible, needs no JS, and is already switched off by the
        prefers-reduced-motion block in index.css.
      */}
      <section className="relative flex-1 py-10 sm:py-12 pb-32 bg-gradient-to-br from-gray-50 to-white overflow-hidden animate-fade-in-up">
        {/* Single subtle shape like About chapter sections – no grid */}
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <header className="mb-8">
            <div className="inline-block px-3 py-1 bg-black/5 border border-black/10 rounded-full mb-3">
              <span className="font-space text-xs font-black text-black tracking-wider uppercase">
                {post.category}
              </span>
            </div>
            <h1 className="font-space text-3xl sm:text-4xl font-black text-black leading-tight mb-3 tracking-tight">
              {post.title}
            </h1>
            <p className="text-black/60 text-base font-medium">
              {post.dateFormatted}
              {post.readTime && (
                <span className="ml-2">· {post.readTime}</span>
              )}
            </p>
          </header>

          <MockExamCTA postSlug={post.slug} />

          <div className="prose-custom">
            {ContentComponent ? (
              <Suspense fallback={<div className="my-8 h-64 animate-pulse rounded-2xl border-2 border-black/10 bg-black/[0.03]" />}>
                <ContentComponent pdfUrl={post.pdfUrl} />
              </Suspense>
            ) : (
              <p className="text-black/60">{t('blog.post.unavailable', { defaultValue: 'Content for this post is not available.' })}</p>
            )}
          </div>
        </div>
      </section>

      {/* Thumb-zone CTA: PDF download – black button to match site */}
      {hasPdf && post.pdfUrl && (
        <div
          className="fixed bottom-0 left-0 right-0 z-20 safe-area-inset-bottom bg-white/95 backdrop-blur-md border-t-2 border-black/10 shadow-[0_-4px_24px_rgba(0,0,0,0.08)]"
          role="complementary"
          aria-label={t('blog.post.downloadAria', { defaultValue: 'Download PDF' })}
        >
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-center gap-3">
            <a
              href={post.pdfUrl}
              download={post.pdfUrl.split('/').pop()}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto min-h-[52px] px-6 py-3 bg-black hover:bg-black/90 text-white font-space font-bold rounded-xl border-2 border-black shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <FileDown className="w-5 h-5 shrink-0" />
              <span>{t('blog.post.downloadPdf', { category: post.category, defaultValue: 'Download {{category}} Redemittel PDF' })}</span>
            </a>
          </div>
        </div>
      )}

      {/* Thumb-zone CTA for posts without a PDF: free mock exam (matches PDF bar styling) */}
      {!hasPdf && (
        <div
          className="fixed bottom-0 left-0 right-0 z-20 safe-area-inset-bottom bg-white/95 backdrop-blur-md border-t-2 border-black/10 shadow-[0_-4px_24px_rgba(0,0,0,0.08)]"
          role="complementary"
          aria-label={t('blog.post.freeExamAria', { defaultValue: 'Free mock exam' })}
        >
          <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-center gap-3">
            <Link
              to={`/${lang}/try`}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto min-h-[52px] px-6 py-3 bg-black hover:bg-black/90 text-white font-space font-bold rounded-xl border-2 border-black shadow-lg transition-all hover:scale-[1.02] active:scale-[0.98]"
            >
              <span>{t('blog.post.freeExam', { defaultValue: 'Start a free mock exam' })}</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
